import { createPortal } from 'react-dom'
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import type { HinweisLage } from './diagramm'

/** Der Kasten am Mauszeiger. Haelt sich innerhalb des Fensters. */
export function Hinweis(
  { lage, children, verankert = false }: {
    lage: HinweisLage
    children: React.ReactNode
    verankert?: boolean
  },
) {
  const rand = 14
  const links = verankert
    ? Math.max(10, Math.min(lage.x - 120, window.innerWidth - 250))
    : Math.min(lage.x + rand, window.innerWidth - 230)
  const oben = verankert ? lage.y + 8 : Math.max(10, lage.y - 16)
  return createPortal(
    <div className={`hinweis${verankert ? ' verankert' : ''}`}
         style={{ left: links, top: oben }}>{children}</div>,
    document.body,
  )
}

/** Ein Hinweis an einem festen Element: Maus, Tastatur und Tippen teilen
 * denselben Kasten wie die Diagramme. */
export function Tipp({ text, children, fokus = false, className, style }: {
  text: React.ReactNode
  children?: React.ReactNode
  fokus?: boolean
  className?: string
  style?: CSSProperties
}) {
  const id = useId()
  const anker = useRef<HTMLSpanElement>(null)
  const [offen, setOffen] = useState(false)
  const [lage, setLage] = useState<HinweisLage>({ x: 0, y: 0 })

  const ausrichten = () => {
    const rect = anker.current?.getBoundingClientRect()
    if (rect) setLage({ x: rect.left + rect.width / 2, y: rect.bottom })
  }
  useLayoutEffect(() => { if (offen) ausrichten() }, [offen])
  useEffect(() => {
    if (!offen) return
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOffen(false)
    }
    const daneben = (e: PointerEvent) => {
      if (!anker.current?.contains(e.target as Node)) setOffen(false)
    }
    const bewegen = () => ausrichten()
    document.addEventListener('keydown', taste)
    document.addEventListener('pointerdown', daneben)
    window.addEventListener('resize', bewegen)
    window.addEventListener('scroll', bewegen, true)
    return () => {
      document.removeEventListener('keydown', taste)
      document.removeEventListener('pointerdown', daneben)
      window.removeEventListener('resize', bewegen)
      window.removeEventListener('scroll', bewegen, true)
    }
  }, [offen])

  return (
    <span ref={anker} className={`tipp-anker${className ? ` ${className}` : ''}`}
          style={style} tabIndex={fokus ? 0 : undefined}
          aria-describedby={offen ? id : undefined}
          onMouseEnter={() => setOffen(true)} onMouseLeave={() => setOffen(false)}
          onFocus={() => setOffen(true)} onBlur={() => setOffen(false)}
          onPointerDown={(e) => {
            if (e.pointerType === 'touch' && children) {
              setOffen((wert) => !wert)
            }
          }}>
      {children ?? (
        <button type="button" className="tipp-knopf" aria-label="Hinweis zeigen"
                aria-expanded={offen} onClick={() => setOffen((wert) => !wert)}>
          <span aria-hidden="true">ℹ</span>
        </button>
      )}
      {offen && (
        <Hinweis lage={lage} verankert>
          <div id={id}>{text}</div>
        </Hinweis>
      )}
    </span>
  )
}

export function HinweisZeile(
  { name, wert }: { name: string; wert: string },
) {
  return (
    <div className="zeile">
      <span className="dezent">{name}</span>
      <span>{wert}</span>
    </div>
  )
}
